import { useEffect, useRef, useState } from "react";
import { Alert, Backdrop, Box, Button, CircularProgress, Fade, Grid, Input, LinearProgress, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { postData } from "../../../services/base.service";
import DocumentPreviewer from "./DocumentPreviewer";
import { removeFileExtension } from "../../../utils/utilFunctions";
import { useNotification } from "../../Shared/NotificationProvider";

const loadingMessages = [
  "Parsing CV sections",
  "Formatting into template",
  "Checking structure and layout",
  "Preparing your download",
];

function CVFormaterView() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFormatting, setIsFormatting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const notify = useNotification();

  useEffect(() => {
    if (!isFormatting) {
      setLoadingStep(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % loadingMessages.length);
    }, 1600);

    return () => window.clearInterval(intervalId);
  }, [isFormatting]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      return;
    }

    const file = event.target.files[0];
    const ext = file.name.split(".").pop()?.toLowerCase();

    if (["pdf", "doc", "docx", "odt"].includes(ext || "")) {
      setSelectedFile(file);
      return;
    }

    notify("Invalid file type. Please select a PDF, DOC, DOCX, or ODT file.", 'warning');
  };

  const handleFormatCV = async () => {
    if (!selectedFile) {
      notify("Please upload a CV first.", 'warning');
      return;
    }

    setIsFormatting(true);

    try {
      const formData = new FormData();
      formData.append("cv", selectedFile);

      const blob = await postData("ai/formatCv", formData, "blob", undefined, false, "none", "*/*");
      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        selectedFile?.name
          ? `${removeFileExtension(selectedFile.name)}_formatted.docx`
          : "formatted_cv.docx"
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error formatting CV:", error);
      notify("Error formatting CV. Please try again.", 'error');
    } finally {
      setIsFormatting(false);
    }
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          CV Formater
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          Upload a CV and click Format CV. The backend will return the formatted template document directly.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Input
            type="file"
            sx={{ display: "none" }}
            inputRef={fileInputRef}
            onChange={handleFileChange}
            inputProps={{ accept: ".pdf,.doc,.docx,.odt" }}
          />

          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedFile ? `Change CV (${selectedFile.name})` : "Upload CV"}
          </Button>

          <Button
            variant="contained"
            startIcon={isFormatting ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
            disabled={!selectedFile || isFormatting}
            onClick={handleFormatCV}
          >
            {isFormatting ? "Formatting..." : "Format CV and Download"}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        {selectedFile ? (
          <DocumentPreviewer file={selectedFile} />
        ) : (
          <Box sx={{ p: 3, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
            <Typography color="text.secondary">Upload a CV file to preview it here.</Typography>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ p: 3, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
          <Typography color="text.secondary">
            The formatted template is downloaded directly after processing.
          </Typography>
        </Box>
      </Grid>

      <Backdrop
        open={isFormatting}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          background: "radial-gradient(circle at 20% 20%, rgba(18, 83, 82, 0.82), rgba(13, 36, 54, 0.94))",
          backdropFilter: "blur(4px)",
        }}
      >
        <Fade in={isFormatting}>
          <Box
            sx={{
              width: { xs: "92%", sm: 520 },
              p: 4,
              borderRadius: 3,
              border: "1px solid rgba(255, 255, 255, 0.22)",
              bgcolor: "rgba(8, 17, 30, 0.55)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CircularProgress size={28} sx={{ color: "#9EE6D8" }} />
              <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
                Formatting CV
              </Typography>
            </Box>

            <Typography sx={{ color: "rgba(255,255,255,0.9)", mb: 2 }}>
              {loadingMessages[loadingStep]}
            </Typography>

            <LinearProgress
              variant="indeterminate"
              sx={{
                height: 9,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.2)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  background: "linear-gradient(90deg, #7CE7D0 0%, #65C8FF 100%)",
                },
              }}
            />

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              {loadingMessages.map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: idx === loadingStep ? "#9EE6D8" : "rgba(255,255,255,0.35)",
                    transition: "all 0.25s ease",
                    transform: idx === loadingStep ? "scale(1.35)" : "scale(1)",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      </Backdrop>
    </Grid>
  );
}

export default CVFormaterView;
